import { normalizeThemeId } from '../config/themes.ts';
import type {
  BackupCounts,
  BackupFileV2,
  BackupPayload,
  Category,
  ChecklistItem,
  Note,
  PlannerData,
  PlannerEvent,
  Settings,
  ShoppingItem,
  Snapshot,
  Task,
  TaskOccurrence
} from '../types';

export const BACKUP_SIGNATURE = 'spooky-plans-backup' as const;
export const BACKUP_SCHEMA_VERSION = 2 as const;
export const MAX_BACKUP_BYTES = 10 * 1024 * 1024;
export const MAX_LOCAL_SNAPSHOTS = 3;

const COLLECTION_KEYS = ['tasks', 'taskOccurrences', 'events', 'notes', 'shoppingItems', 'categories'] as const;
const ROOT_KEYS = new Set(['signature', 'schemaVersion', 'appVersion', 'exportedAt', 'counts', 'settings', ...COLLECTION_KEYS]);
const MAX_COLLECTION_ITEMS = 100_000;
const MAX_TEXT = 100_000;
const MAX_SHORT_TEXT = 500;
const BASE_KEYS = ['id', 'categoryId', 'favorite', 'createdAt', 'updatedAt'] as const;
const COUNT_KEYS = [...COLLECTION_KEYS] as const;
const PRIORITIES = ['low', 'medium', 'high'] as const;
const TASK_STATUSES = ['pending', 'in-progress', 'completed'] as const;
const RECURRENCES = ['none', 'daily', 'weekly', 'monthly'] as const;

export class BackupValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BackupValidationError';
  }
}

const fail = (message: string): never => {
  throw new BackupValidationError(message);
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const record = (value: unknown, path: string) => isRecord(value) ? value : fail(`${path} must be an object.`);
const exactKeys = (source: Record<string, unknown>, allowed: readonly string[], path: string) => {
  const allowedKeys = new Set(allowed);
  for (const key of Object.keys(source)) if (!allowedKeys.has(key)) fail(`${path} contains unsupported field "${key}".`);
  return source;
};
const text = (value: unknown, path: string, max = MAX_SHORT_TEXT) =>
  typeof value === 'string' && value.length <= max ? value : fail(`${path} must be text with at most ${max} characters.`);
const requiredText = (value: unknown, path: string, max: number) => {
  const result = text(value, path, max);
  return result.trim() ? result : fail(`${path} cannot be empty.`);
};
const boolean = (value: unknown, path: string) => typeof value === 'boolean' ? value : fail(`${path} must be true or false.`);
const nullableText = (value: unknown, path: string, max = MAX_SHORT_TEXT) => value === null ? null : text(value, path, max);
const oneOf = <T extends string | number>(value: unknown, values: readonly T[], path: string): T =>
  values.includes(value as T) ? value as T : fail(`${path} has an unsupported value.`);

const isoDateTime = (value: unknown, path: string) => {
  const result = text(value, path, 50);
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(result) || Number.isNaN(Date.parse(result)) || new Date(result).toISOString() !== result) fail(`${path} must be a valid ISO date and time.`);
  return result;
};

const civilDate = (value: unknown, path: string) => {
  const result = text(value, path, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(result);
  if (!match) return fail(`${path} must use YYYY-MM-DD.`);
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  if (date.toISOString().slice(0, 10) !== result) fail(`${path} is not a valid calendar date.`);
  return result;
};

const nullableDate = (value: unknown, path: string) => value === null ? null : civilDate(value, path);
const nullableTime = (value: unknown, path: string) => {
  if (value === null) return null;
  const result = text(value, path, 5);
  if (!/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(result)) fail(`${path} must use a valid 24-hour HH:mm value.`);
  return result;
};

const array = (value: unknown, path: string, maxItems = MAX_COLLECTION_ITEMS): unknown[] => {
  if (!Array.isArray(value)) return fail(`${path} must be a list.`);
  if (value.length > maxItems) fail(`${path} has too many items. Maximum: ${maxItems}.`);
  return value;
};

const checklist = (value: unknown, path: string, maxItems: number): ChecklistItem[] => {
  const ids = new Set<string>();
  return array(value, path, maxItems).map((item, index) => {
    const source = exactKeys(record(item, `${path}[${index}]`), ['id', 'text', 'completed'], `${path}[${index}]`);
    const id = text(source.id, `${path}[${index}].id`);
    if (!id.trim()) fail(`${path}[${index}].id cannot be empty.`);
    if (ids.has(id)) fail(`${path} contains duplicate id "${id}".`);
    ids.add(id);
    return {
      id,
      text: text(source.text, `${path}[${index}].text`, MAX_TEXT),
      completed: boolean(source.completed, `${path}[${index}].completed`)
    };
  });
};

const base = (source: Record<string, unknown>, path: string) => ({
  id: text(source.id, `${path}.id`),
  categoryId: nullableText(source.categoryId, `${path}.categoryId`),
  favorite: boolean(source.favorite, `${path}.favorite`),
  createdAt: isoDateTime(source.createdAt, `${path}.createdAt`),
  updatedAt: isoDateTime(source.updatedAt, `${path}.updatedAt`)
});

const parseTasks = (value: unknown): Task[] => array(value, 'tasks').map((item, index) => {
  const path = `tasks[${index}]`;
  const source = exactKeys(record(item, path), [...BASE_KEYS, 'title', 'description', 'checklist', 'priority', 'status', 'dueDate', 'dueTime', 'recurrence', 'reminder', 'completedAt'], path);
  return {
    ...base(source, path),
    title: requiredText(source.title, `${path}.title`, 200),
    description: text(source.description, `${path}.description`, 5_000),
    checklist: checklist(source.checklist, `${path}.checklist`, 100),
    priority: oneOf(source.priority, PRIORITIES, `${path}.priority`),
    status: oneOf(source.status, TASK_STATUSES, `${path}.status`),
    dueDate: nullableDate(source.dueDate, `${path}.dueDate`),
    dueTime: nullableTime(source.dueTime, `${path}.dueTime`),
    recurrence: oneOf(source.recurrence, RECURRENCES, `${path}.recurrence`),
    reminder: nullableTime(source.reminder, `${path}.reminder`),
    completedAt: source.completedAt === null ? null : isoDateTime(source.completedAt, `${path}.completedAt`)
  };
});

const parseOccurrences = (value: unknown): TaskOccurrence[] => array(value, 'taskOccurrences').map((item, index) => {
  const path = `taskOccurrences[${index}]`;
  const source = exactKeys(record(item, path), ['id', 'taskId', 'occurrenceDate', 'completed', 'completedAt', 'updatedAt'], path);
  return {
    id: text(source.id, `${path}.id`),
    taskId: text(source.taskId, `${path}.taskId`),
    occurrenceDate: civilDate(source.occurrenceDate, `${path}.occurrenceDate`),
    completed: boolean(source.completed, `${path}.completed`),
    completedAt: source.completedAt === null ? null : isoDateTime(source.completedAt, `${path}.completedAt`),
    updatedAt: isoDateTime(source.updatedAt, `${path}.updatedAt`)
  };
});

const parseEvents = (value: unknown): PlannerEvent[] => array(value, 'events').map((item, index) => {
  const path = `events[${index}]`;
  const source = exactKeys(record(item, path), [...BASE_KEYS, 'title', 'description', 'checklist', 'date', 'startTime', 'endTime', 'location', 'recurrence', 'reminder'], path);
  const event = {
    ...base(source, path),
    title: requiredText(source.title, `${path}.title`, 200),
    description: text(source.description, `${path}.description`, 5_000),
    checklist: checklist(source.checklist, `${path}.checklist`, 100),
    date: civilDate(source.date, `${path}.date`),
    startTime: nullableTime(source.startTime, `${path}.startTime`),
    endTime: nullableTime(source.endTime, `${path}.endTime`),
    location: text(source.location, `${path}.location`, 300),
    recurrence: oneOf(source.recurrence, RECURRENCES, `${path}.recurrence`),
    reminder: nullableTime(source.reminder, `${path}.reminder`)
  };
  if (event.startTime && event.endTime && event.endTime < event.startTime) fail(`${path}.endTime cannot be earlier than startTime.`);
  return event;
});

const parseNotes = (value: unknown): Note[] => array(value, 'notes').map((item, index) => {
  const path = `notes[${index}]`;
  const source = exactKeys(record(item, path), [...BASE_KEYS, 'title', 'content', 'checklist', 'pinned'], path);
  return {
    ...base(source, path),
    title: text(source.title, `${path}.title`, 200),
    content: text(source.content, `${path}.content`, 50_000),
    checklist: checklist(source.checklist, `${path}.checklist`, 200),
    pinned: boolean(source.pinned, `${path}.pinned`)
  };
});

const parseShoppingItems = (value: unknown): ShoppingItem[] => array(value, 'shoppingItems').map((item, index) => {
  const path = `shoppingItems[${index}]`;
  const source = exactKeys(record(item, path), [...BASE_KEYS, 'name', 'quantity', 'store', 'priority', 'notes', 'completed'], path);
  return {
    ...base(source, path),
    name: requiredText(source.name, `${path}.name`, 200),
    quantity: text(source.quantity, `${path}.quantity`, 100),
    store: text(source.store, `${path}.store`, 200),
    priority: oneOf(source.priority, PRIORITIES, `${path}.priority`),
    notes: text(source.notes, `${path}.notes`, 2_000),
    completed: boolean(source.completed, `${path}.completed`)
  };
});

const parseCategories = (value: unknown): Category[] => array(value, 'categories').map((item, index) => {
  const path = `categories[${index}]`;
  const source = exactKeys(record(item, path), ['id', 'name', 'normalizedName', 'color', 'icon', 'kind'], path);
  const color = text(source.color, `${path}.color`, 20);
  const name = requiredText(source.name, `${path}.name`, 80);
  const normalizedName = text(source.normalizedName, `${path}.normalizedName`, 80);
  if (!/^#[0-9a-f]{6}$/i.test(color)) fail(`${path}.color must be a six-digit hex color.`);
  if (!name.trim()) fail(`${path}.name cannot be empty.`);
  if (name.trim().toLocaleLowerCase('en').normalize('NFKC') !== normalizedName) fail(`${path}.normalizedName does not match its category name.`);
  return {
    id: text(source.id, `${path}.id`),
    name,
    normalizedName,
    color,
    icon: nullableText(source.icon, `${path}.icon`, 80),
    kind: oneOf(source.kind, ['default', 'custom'] as const, `${path}.kind`)
  };
});

const parseSettings = (value: unknown): Settings => {
  const source = exactKeys(record(value, 'settings'), ['schemaVersion', 'themeId', 'weekStartsOn', 'timeFormat'], 'settings');
  if (source.schemaVersion !== 4) fail('settings.schemaVersion must be 4.');
  const themeId = normalizeThemeId(source.themeId);
  if (!themeId) return fail('settings.themeId is not a supported planner color.');
  return {
    schemaVersion: 4,
    themeId,
    weekStartsOn: oneOf(source.weekStartsOn, [0, 1] as const, 'settings.weekStartsOn'),
    timeFormat: oneOf(source.timeFormat, [12, 24] as const, 'settings.timeFormat')
  };
};

export const getBackupCounts = (data: PlannerData): BackupCounts => ({
  tasks: data.tasks.length,
  taskOccurrences: data.taskOccurrences.length,
  events: data.events.length,
  notes: data.notes.length,
  shoppingItems: data.shoppingItems.length,
  categories: data.categories.length
});

export const getSnapshotIdsToDelete = (existing: readonly Pick<Snapshot, 'id' | 'createdAt'>[]) => {
  const retainedIds = new Set([...existing]
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .slice(0, MAX_LOCAL_SNAPSHOTS - 1)
    .map(({ id }) => id));
  return existing.filter(({ id }) => !retainedIds.has(id)).map(({ id }) => id);
};

const validateUniqueIds = (data: PlannerData) => {
  for (const key of COLLECTION_KEYS) {
    const ids = new Set<string>();
    for (const item of data[key]) {
      if (!item.id.trim()) fail(`${key} contains an empty id.`);
      if (ids.has(item.id)) fail(`${key} contains duplicate id "${item.id}".`);
      ids.add(item.id);
    }
  }
  const categoryNames = new Set<string>();
  for (const category of data.categories) {
    if (categoryNames.has(category.normalizedName)) fail(`categories contains duplicate normalized name "${category.normalizedName}".`);
    categoryNames.add(category.normalizedName);
  }
  const occurrenceKeys = new Set<string>();
  for (const occurrence of data.taskOccurrences) {
    const key = `${occurrence.taskId}\u0000${occurrence.occurrenceDate}`;
    if (occurrenceKeys.has(key)) fail(`taskOccurrences contains more than one entry for task "${occurrence.taskId}" on ${occurrence.occurrenceDate}.`);
    occurrenceKeys.add(key);
  }
};

const validateRelations = (data: PlannerData) => {
  const categoryIds = new Set(data.categories.map((item) => item.id));
  for (const key of ['tasks', 'events', 'notes', 'shoppingItems'] as const) {
    for (const item of data[key]) {
      if (item.categoryId !== null && !categoryIds.has(item.categoryId)) fail(`${key} item "${item.id}" references a missing category.`);
    }
  }
  const taskIds = new Set(data.tasks.map((item) => item.id));
  for (const occurrence of data.taskOccurrences) {
    if (!taskIds.has(occurrence.taskId)) fail(`Task occurrence "${occurrence.id}" references a missing task.`);
  }
};

const parsePayload = (source: Record<string, unknown>): BackupPayload => {
  const data: PlannerData = {
    tasks: parseTasks(source.tasks),
    taskOccurrences: parseOccurrences(source.taskOccurrences),
    events: parseEvents(source.events),
    notes: parseNotes(source.notes),
    shoppingItems: parseShoppingItems(source.shoppingItems),
    categories: parseCategories(source.categories)
  };
  validateUniqueIds(data);
  validateRelations(data);
  return { ...data, settings: parseSettings(source.settings) };
};

const validateCounts = (value: unknown, expected: BackupCounts) => {
  const source = exactKeys(record(value, 'counts'), COUNT_KEYS, 'counts');
  for (const key of COLLECTION_KEYS) {
    if (!Number.isSafeInteger(source[key]) || source[key] !== expected[key]) fail(`counts.${key} does not match the backup contents.`);
  }
  return expected;
};

export const buildBackup = (data: PlannerData, settings: Settings, appVersion: string, exportedAt = new Date().toISOString()): BackupFileV2 => ({
  signature: BACKUP_SIGNATURE,
  schemaVersion: BACKUP_SCHEMA_VERSION,
  appVersion,
  exportedAt,
  counts: getBackupCounts(data),
  settings,
  ...data
});

export const validateBackupObject = (value: unknown): BackupFileV2 => {
  const source = record(value, 'Backup');
  for (const key of Object.keys(source)) if (!ROOT_KEYS.has(key)) fail(`Backup contains unsupported field "${key}".`);
  if (source.signature !== BACKUP_SIGNATURE) fail('This is not a Spooky Plan backup.');
  if (source.schemaVersion !== BACKUP_SCHEMA_VERSION) fail('This backup version is not supported. Export a schema 2 backup from Spooky Plan.');
  const payload = parsePayload(source);
  const counts = validateCounts(source.counts, getBackupCounts(payload));
  return {
    signature: BACKUP_SIGNATURE,
    schemaVersion: BACKUP_SCHEMA_VERSION,
    appVersion: text(source.appVersion, 'appVersion', 50),
    exportedAt: isoDateTime(source.exportedAt, 'exportedAt'),
    counts,
    ...payload
  };
};

export const validateBackupText = (contents: string, byteSize: number): BackupFileV2 => {
  if (byteSize > MAX_BACKUP_BYTES) fail('The backup is larger than the 10 MiB limit. Your current planner was not changed.');
  let parsed: unknown;
  try {
    parsed = JSON.parse(contents);
  } catch {
    fail('The backup is not valid JSON. Your current planner was not changed.');
  }
  return validateBackupObject(parsed);
};

export const validateSnapshot = (value: unknown): Snapshot => {
  const source = exactKeys(record(value, 'Snapshot'), ['id', 'createdAt', 'appVersion', 'schemaVersion', 'counts', 'payload'], 'Snapshot');
  if (source.schemaVersion !== BACKUP_SCHEMA_VERSION) fail('This snapshot version is not supported.');
  const payloadSource = exactKeys(record(source.payload, 'Snapshot payload'), ['settings', ...COLLECTION_KEYS], 'Snapshot payload');
  const payload = parsePayload(payloadSource);
  return {
    id: text(source.id, 'Snapshot.id'),
    createdAt: isoDateTime(source.createdAt, 'Snapshot.createdAt'),
    appVersion: text(source.appVersion, 'Snapshot.appVersion', 50),
    schemaVersion: BACKUP_SCHEMA_VERSION,
    counts: validateCounts(source.counts, getBackupCounts(payload)),
    payload
  };
};

export const backupFileName = (date = new Date()) => `spooky-plans-backup-${date.toISOString().slice(0, 10)}.json`;
